class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (cartLink) {
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          event.stopPropagation();
          this.open(cartLink);
        }
      });
    }

    if (this._holoDocClickBound) return;
    this._holoDocClickBound = true;
    this._onDocClick = this._onDocClick.bind(this);
    document.addEventListener('click', this._onDocClick, true);
  }

  _onDocClick(event) {
    const closeBtn = event.target.closest?.('.drawer__close');
    if (closeBtn) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      this.close();
      return;
    }

    const overlay = event.target.closest?.('#CartDrawer-Overlay');
    if (overlay) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      this.close();
      return;
    }

    const cartLink = event.target.closest?.('#cart-icon-bubble');
    if (!cartLink) return;

    // Header bag opens the drawer. Never follow href="/cart".
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    if (this._blockCartIconUntil && Date.now() < this._blockCartIconUntil) return;
    if (this.classList.contains('active') || this._opening || this._wantOpen) return;
    this.open(cartLink);
  }

  shopRoot() {
    return (window.Shopify?.routes?.root) || '/';
  }

  /**
   * Pull the live Shopify cart into the drawer + header bubble.
   * HOLO add-to-cart only used to refresh the icon, so the drawer could
   * stay on the empty HTML from page load.
   */
  refreshFromShopify() {
    if (this._refreshPromise) return this._refreshPromise;
    const root = this.shopRoot();
    const cartUrl = (typeof routes !== 'undefined' && routes.cart_url) || `${root}cart`;

    this._refreshPromise = Promise.all([
      fetch(`${root}cart.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      }).then((res) => (res.ok ? res.json() : null)).catch(() => null),
      fetch(`${root}?sections=cart-drawer,cart-icon-bubble`, {
        credentials: 'same-origin',
        cache: 'no-store',
      }).then((res) => (res.ok ? res.json() : null)).catch(() => null),
    ])
      .then(async ([cart, sections]) => {
        const itemCount = Number(cart?.item_count) || 0;
        let sectionHtml = sections?.['cart-drawer'] || '';
        if (!sectionHtml) {
          try {
            const fallback = await fetch(`${cartUrl}?section_id=cart-drawer`, {
              credentials: 'same-origin',
              cache: 'no-store',
            });
            if (fallback.ok) sectionHtml = await fallback.text();
          } catch {
            sectionHtml = '';
          }
        }

        if (sectionHtml) {
          const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
          const incoming = doc.querySelector('#CartDrawer');
          const current = this.querySelector('#CartDrawer');
          if (incoming && current) {
            current.innerHTML = incoming.innerHTML;
          }
        }

        this.classList.toggle('is-empty', itemCount === 0);
        this.querySelector('cart-drawer-items')?.classList.toggle('is-empty', itemCount === 0);

        const overlay = this.querySelector('#CartDrawer-Overlay');
        if (overlay && !overlay.dataset.holoCloseBound) {
          overlay.addEventListener('click', this.close.bind(this));
          overlay.dataset.holoCloseBound = 'true';
        }

        const markup = sections?.['cart-icon-bubble'];
        const target = document.getElementById('cart-icon-bubble');
        if (markup && target) {
          const parsed = new DOMParser().parseFromString(markup, 'text/html');
          const next = parsed.querySelector('#cart-icon-bubble');
          if (next) target.innerHTML = next.innerHTML;
          else {
            const sectionRoot = parsed.querySelector('.shopify-section') || parsed.body;
            if (sectionRoot) target.innerHTML = sectionRoot.innerHTML;
          }
        }

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'holo-cart-sync',
            cartData: cart ?? undefined,
          });
        }

        return cart;
      })
      .finally(() => {
        this._refreshPromise = null;
      });

    return this._refreshPromise;
  }

  open(triggeredBy) {
    if (this.classList.contains('active') || this._opening) return;
    this._opening = true;
    this._wantOpen = true;
    if (triggeredBy) this.setActiveElement(triggeredBy);

    const reveal = () => {
      this._opening = false;
      if (!this._wantOpen || this.classList.contains('active')) return;
      const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
      if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
      this.promoteToOverlay();
      setTimeout(() => {
        if (!this._wantOpen) return;
        this.classList.add('animate', 'active');
        document.body.classList.add('holo-cart-drawer-open');
      });

      this.addEventListener(
        'transitionend',
        () => {
          if (!this._wantOpen) return;
          const containerToTrapFocusOn = this.classList.contains('is-empty')
            ? this.querySelector('.drawer__inner-empty')
            : document.getElementById('CartDrawer');
          const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
          trapFocus(containerToTrapFocusOn, focusElement);
        },
        { once: true },
      );

      document.body.classList.add('overflow-hidden');
      this.querySelector('cart-drawer-items')?.dispatchViewEvent?.();
    };

    this.refreshFromShopify().then(reveal).catch(reveal);
  }

  promoteToOverlay() {
    if (typeof this.showPopover === 'function') {
      try {
        this.setAttribute('popover', 'manual');
        if (!this.matches(':popover-open')) this.showPopover();
        return;
      } catch {
        this.removeAttribute('popover');
      }
    }

    if (this.parentElement !== document.body) {
      this._homeParent = this.parentElement;
      this._homeNext = this.nextSibling;
      document.body.appendChild(this);
    }
  }

  restoreFromOverlay() {
    if (this.hasAttribute('popover')) {
      try {
        if (this.matches(':popover-open')) this.hidePopover();
      } catch {
        /* already closed */
      }
      this.removeAttribute('popover');
    }

    if (this._homeParent) {
      this._homeParent.insertBefore(this, this._homeNext);
      this._homeParent = null;
      this._homeNext = null;
    }
  }

  close() {
    this._wantOpen = false;
    this._opening = false;
    this._blockCartIconUntil = Date.now() + 700;
    this.classList.remove('active');
    document.body.classList.remove('holo-cart-drawer-open');
    document.body.classList.add('holo-cart-drawer-closing');
    window.clearTimeout(this._closingClassTimer);
    this._closingClassTimer = window.setTimeout(() => {
      document.body.classList.remove('holo-cart-drawer-closing');
    }, 700);
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    window.setTimeout(() => this.restoreFromOverlay(), 80);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.classList.remove('is-empty');
    this.querySelector('.drawer__inner')?.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      const html = parsedState.sections?.[section.id];
      if (!html) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(html, section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

window.HoloCartSync = {
  refresh() {
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.refreshFromShopify === 'function') {
      return drawer.refreshFromShopify();
    }
    return Promise.resolve(null);
  },
};
